import React, { useState, useCallback, useEffect } from 'react';
import './App.css';
import Header from './components/Header';
import ImmeubleSelector from './components/ImmeubleSelector';
import UploadZone from './components/UploadZone';
import ProcessSteps from './components/ProcessSteps';
import DataTable from './components/DataTable';
import ExportButton from './components/ExportButton';
import ValidationModal from './components/ValidationModal';
import LoginPage from './components/LoginPage';
import ProfileForm from './components/ProfileForm';
import { uploadSingleFile, exportToExcel, sendToN8n, finalizeDocuments, healthCheck, setAuth, getToken, getStoredUser, getMe, saveCorrections } from './services/api';

const TRACKED_FIELDS = [
  'assureur', 'numeroContrat', 'nomSyndic', 'nomCopropriete',
  'adresseImmeuble', 'cpImmeuble', 'villeImmeuble', 'echeanceAnnuelle',
  'dateDebutContrat', 'nomAgent'
];

function computeDiff(originals, corrected) {
  const corrections = [];
  originals.forEach((original) => {
    const corr = corrected.find(d => d.id === original.id);
    if (!corr) return;
    TRACKED_FIELDS.forEach(field => {
      if (String(original[field] ?? '') !== String(corr[field] ?? '')) {
        corrections.push({
          field,
          originalValue: original[field],
          correctedValue: corr[field],
          context: { assureur: original.assureur, numeroContrat: original.numeroContrat }
        });
      }
    });
  });
  return corrections;
}

function App() {
  const [files, setFiles] = useState([]);
  const [extractedData, setExtractedData] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [fileStatuses, setFileStatuses] = useState({});
  const [processedCount, setProcessedCount] = useState(0);
  const [immeubleSelection, setImmeubleSelection] = useState(null);

  // États de la validation
  const [pendingValidationData, setPendingValidationData] = useState([]);
  const [pendingUploadedFiles, setPendingUploadedFiles] = useState([]);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationSuccess, setValidationSuccess] = useState(false);

  // Auth
  const [appReady, setAppReady] = useState(false);
  const [isOnline, setIsOnline] = useState(false);
  const [user, setUser] = useState(null);
  const [serverInfo, setServerInfo] = useState(null);
  const [updateInfo, setUpdateInfo] = useState(null);

  // Au démarrage : vérifier le mode et restaurer la session
  useEffect(() => {
    async function checkServer() {
      const info = await healthCheck();
      setServerInfo(info);

      if (info.status === 'ok' && info.mode === 'online') {
        setIsOnline(true);
        // Restaurer la session si un token existe
        const token = getToken();
        if (token) {
          try {
            const data = await getMe();
            setUser(data.user);
          } catch {
            // Token invalide, on le supprime
            setAuth(null);
          }
        }
        // Vérifier si on arrive via un magic link
        const params = new URLSearchParams(window.location.search);
        const magicToken = params.get('token');
        if (magicToken && window.location.pathname === '/auth/magic') {
          try {
            const res = await fetch('/api/auth/magic-link/verify', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ token: magicToken })
            });
            const data = await res.json();
            if (data.token) {
              setAuth(data.token, data.user);
              setUser(data.user);
              window.history.replaceState({}, '', '/');
            }
          } catch {
            // Lien invalide, on ignore
          }
        }
      } else if (info.status === 'ok' && info.mode === 'desktop') {
        // Mode desktop : vérifier les mises à jour
        try {
          const updateRes = await fetch('/api/check-update');
          const update = await updateRes.json();
          if (update.updateAvailable) {
            setUpdateInfo(update);
          }
        } catch {}
      }
      setAppReady(true);
    }
    checkServer();

    // Écouter les événements 401
    const handleAuthRequired = () => {
      setUser(null);
      setAuth(null);
    };
    window.addEventListener('auth-required', handleAuthRequired);
    return () => window.removeEventListener('auth-required', handleAuthRequired);
  }, []);

  const handleLogin = (token, userData) => {
    setAuth(token, userData);
    setUser(userData);
  };

  const [editingProfile, setEditingProfile] = useState(false);

  const handleProfileComplete = (updatedUser) => {
    setUser(updatedUser);
    setEditingProfile(false);
    localStorage.setItem('assurextract_user', JSON.stringify(updatedUser));
  };

  const handleLogout = () => {
    setAuth(null);
    setUser(null);
  };

  // Masquer la bannière de succès après 5s
  useEffect(() => {
    if (!validationSuccess) return;
    const t = setTimeout(() => setValidationSuccess(false), 5000);
    return () => clearTimeout(t);
  }, [validationSuccess]);

  const handleFilesSelected = (selectedFiles) => {
    setFiles(prevFiles => [...prevFiles, ...selectedFiles]);
    setError(null);
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setError('Veuillez sélectionner des fichiers');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setProcessedCount(0);

    const initialStatuses = {};
    files.forEach((file, index) => {
      initialStatuses[index] = 'pending';
    });
    setFileStatuses(initialStatuses);

    let errorCount = 0;
    const allExtractedData = [];
    const allUploadedFiles = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setFileStatuses(prev => ({ ...prev, [i]: 'processing' }));

      try {
        const selectionData = immeubleSelection ? {
          typeImmeuble: immeubleSelection.typeImmeuble,
          typeProprietaire: immeubleSelection.typeProprietaire,
          ...(immeubleSelection.typeProprietaire === 'syndic_professionnel' && immeubleSelection.syndicData ? {
            nomSyndic: immeubleSelection.syndicData.nom,
            siretSyndic: immeubleSelection.syndicData.siret,
            adresseSyndic: immeubleSelection.syndicData.adresse,
            cpSyndic: immeubleSelection.syndicData.codePostal,
            villeSyndic: immeubleSelection.syndicData.ville,
            codeNafSyndic: immeubleSelection.syndicData.codeNaf
          } : {})
        } : null;

        const result = await uploadSingleFile(file, selectionData);

        if (result.success && Array.isArray(result.data) && result.data.length > 0) {
          setFileStatuses(prev => ({ ...prev, [i]: 'success' }));
          allExtractedData.push(...result.data);
          // Stocker les chemins des fichiers uploadés pour la finalisation
          if (result._uploadedFiles) {
            allUploadedFiles.push(...result._uploadedFiles);
          }
        } else {
          // data vide = extraction échouée (API Claude surchargée ou PDF illisible)
          setFileStatuses(prev => ({ ...prev, [i]: 'error' }));
          errorCount++;
        }
      } catch (err) {
        setFileStatuses(prev => ({ ...prev, [i]: 'error' }));
        errorCount++;
        console.error(`Erreur traitement ${file.name}:`, err);
      }

      setProcessedCount(i + 1);
    }

    if (allExtractedData.length > 0) {
      // Enrichir avec typeImmeuble / typeProprietaire / données syndic
      const enrichedData = allExtractedData.map(doc => {
        const enriched = {
          typeImmeuble: immeubleSelection?.typeImmeuble || null,
          typeProprietaire: immeubleSelection?.typeProprietaire || null,
          ...doc
        };
        if (immeubleSelection?.typeProprietaire === 'syndic_professionnel' && immeubleSelection.syndicData) {
          return {
            ...enriched,
            nomSyndic: immeubleSelection.syndicData.nom,
            siretSyndic: immeubleSelection.syndicData.siret,
            adresseSyndic: immeubleSelection.syndicData.adresse,
            cpSyndic: immeubleSelection.syndicData.codePostal,
            villeSyndic: immeubleSelection.syndicData.ville,
            codeNafSyndic: immeubleSelection.syndicData.codeNaf
          };
        }
        return enriched;
      });

      // Ouvrir la modal de validation (au lieu d'envoyer automatiquement).
      // CUMUL : on AJOUTE à la file en attente (dépôt en 2 temps) au lieu de remplacer,
      // sinon les documents déjà en attente d'un dépôt précédent disparaissent.
      setPendingValidationData(prev => {
        const merged = [...prev, ...enrichedData];
        return merged.filter((item, idx, self) => idx === self.findIndex(t => t.id === item.id));
      });
      setPendingUploadedFiles(prev => [...prev, ...allUploadedFiles]);
      setShowValidationModal(true);
    }

    if (errorCount > 0) {
      setError(`${errorCount} fichier(s) en erreur sur ${files.length}`);
    }

    setFiles([]);
    setFileStatuses({});
    setIsProcessing(false);
  };

  // Appelé quand l'utilisateur valide la modal
  const handleValidate = async (correctedData) => {
    // 1. Calculer le diff et enregistrer les corrections
    const corrections = computeDiff(pendingValidationData, correctedData);
    if (corrections.length > 0) {
      console.log(`📝 ${corrections.length} correction(s) à enregistrer`);
      await saveCorrections(corrections);
    }

    // 2. Envoyer vers n8n EN PREMIER (création / rattachement Airtable) : le rangement GED de
    // l'étape suivante a besoin que le contrat existe déjà, y compris quand il vient d'être créé.
    let riResults = null;
    try {
      const n8nResult = await sendToN8n(correctedData, {
        telephone: user?.telephone || null,
        nomAgent: user?.nom && user?.prenom ? `${user.prenom} ${user.nom}` : null,
        emailAgent: user?.email || null,
        nbFichiers: pendingUploadedFiles.length || correctedData.length,
        nbDocuments: correctedData.length,
        methode: 'Claude Vision'
      });
      riResults = n8nResult?.resultRI || null;
      console.log(`✅ Webhook envoyé : ${correctedData.length} document(s)`);
    } catch (err) {
      console.error('❌ Erreur envoi webhook :', err);
    }

    // 3. Finaliser : renommage + rangement dans la GED (le serveur attend jusqu'à 90 s qu'un
    // contrat tout juste créé apparaisse ; ce qui ne peut pas être rangé part dans « À classer »).
    let finalData = correctedData;
    try {
      const finalResult = await finalizeDocuments(correctedData, pendingUploadedFiles, riResults);
      if (finalResult.success && finalResult.data) {
        finalData = finalResult.data;
        console.log(`✅ Finalisation OK : ${finalData.length} document(s) renommé(s) et rangé(s)`);
      }
    } catch (err) {
      console.error('❌ Erreur finalisation (renommage/GED) :', err);
      setError('⚠️ Les données sont envoyées vers Airtable, mais le rangement des fichiers dans la GED a échoué : ' + (err.message || 'erreur inconnue'));
    }

    // 4. Ajouter à extractedData (cumul + dédoublonnage)
    setExtractedData(prevData => {
      const newData = [...prevData, ...finalData];
      return newData.filter((item, index, self) =>
        index === self.findIndex(t => t.id === item.id)
      );
    });

    // 5. Mettre à jour les stats
    setStats(prev => ({
      totalFiles: (prev?.totalFiles || 0) + finalData.length,
      extractedCount: (prev?.extractedCount || 0) + finalData.length,
      deduplicatedCount: finalData.length
    }));

    // 6. Réinitialiser la modal
    setPendingValidationData([]);
    setPendingUploadedFiles([]);
    setShowValidationModal(false);

    // 7. Afficher la bannière de succès
    setValidationSuccess(true);
  };

  const handleCloseModal = (remainingData) => {
    setShowValidationModal(false);
    // Persiste l'état courant de la modale (suppressions incluses) : si l'utilisateur
    // a jeté des documents, ils ne doivent pas réapparaître. Si tout est supprimé,
    // la bannière "en attente de validation" disparaît.
    if (Array.isArray(remainingData)) {
      setPendingValidationData(remainingData);
      if (remainingData.length === 0) setPendingUploadedFiles([]);
    }
  };

  const handleExport = async () => {
    if (extractedData.length === 0) {
      setError('Aucune donnée à exporter');
      return;
    }
    try {
      const result = await exportToExcel(extractedData);
      if (result.success) {
        // Télécharger avec le token JWT (window.open ne l'envoie pas)
        const token = getToken();
        const response = await fetch(result.downloadUrl, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = result.filename || 'AssurExtract.xlsx';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      } else {
        setError(result.error || 'Erreur lors de l\'export');
      }
    } catch (err) {
      setError(err.message || 'Erreur lors de l\'export');
    }
  };

  const handleDeleteDocument = useCallback((documentId) => {
    setExtractedData(prevData => prevData.filter(doc => doc.id !== documentId));
    setStats(prev => prev ? {
      ...prev,
      deduplicatedCount: prev.deduplicatedCount - 1
    } : null);
  }, []);

  const handleUpdateDocument = useCallback((documentId, updatedData) => {
    setExtractedData(prevData =>
      prevData.map(doc =>
        doc.id === documentId ? { ...doc, ...updatedData } : doc
      )
    );
  }, []);

  const handleReset = () => {
    setFiles([]);
    setFileStatuses({});
    setProcessedCount(0);
  };

  const handleClearAll = () => {
    setExtractedData([]);
    setStats(null);
    setFiles([]);
    setError(null);
  };

  // Écran de chargement
  if (!appReady) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a', color: '#94a3b8' }}>
        Chargement...
      </div>
    );
  }

  // Mode online : login requis
  if (isOnline && !user) {
    return <LoginPage onLogin={handleLogin} microsoftClientId={serverInfo?.microsoftClientId} microsoftTenantId={serverInfo?.microsoftTenantId} />;
  }

  // Profil incomplet ou édition en cours : formulaire de complétion
  if (isOnline && user && (!user.profileComplete || editingProfile)) {
    return <ProfileForm user={user} onComplete={handleProfileComplete} onCancel={user.profileComplete ? () => setEditingProfile(false) : null} />;
  }

  return (
    <div className="App">
      <Header user={user} onLogout={isOnline ? handleLogout : null} onEditProfile={() => setEditingProfile(true)} version={serverInfo?.version} />

      {updateInfo && updateInfo.updateAvailable && (
        <div style={{
          background: 'linear-gradient(135deg, #f59e0b, #d97706)',
          color: '#fff',
          padding: '10px 20px',
          textAlign: 'center',
          fontSize: '14px',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px'
        }}>
          <span>Une nouvelle version est disponible : v{updateInfo.latestVersion} (vous utilisez v{updateInfo.currentVersion})</span>
          <a
            href={updateInfo.downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              background: '#fff',
              color: '#d97706',
              padding: '4px 16px',
              borderRadius: '6px',
              fontWeight: '700',
              textDecoration: 'none',
              fontSize: '13px'
            }}
          >
            Mettre à jour
          </a>
          <button
            onClick={() => setUpdateInfo(null)}
            style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '18px', padding: '0 4px' }}
          >
            ×
          </button>
        </div>
      )}

      <main className="main-content">
        <div className="hero-section">
          <div className="ai-badge">
            <span className="sparkle">✨</span>
            Propulsé par l'IA
          </div>
          <h1 className="hero-title">
            Transformez vos documents d'assurance
            <br />
            <span className="highlight">en données exploitables</span>
          </h1>
          <p className="hero-subtitle">
            Importez vos appels de prime et contrats au format PDF. Notre IA extrait
            <br />
            automatiquement les informations clés et génère un fichier Excel prêt à l'emploi.
          </p>
        </div>

        <ProcessSteps currentStep={extractedData.length > 0 ? 3 : files.length > 0 ? 1 : 0} />

        {error && (
          <div className="error-banner">
            <span className="error-icon">⚠️</span>
            {error}
            <button className="error-close" onClick={() => setError(null)}>×</button>
          </div>
        )}

        {/* Bannière verte — validation réussie */}
        {validationSuccess && (
          <div className="success-banner">
            <span>✅</span>
            Documents validés et envoyés vers Airtable avec succès !
          </div>
        )}

        {/* Bannière jaune sticky — documents en attente de validation */}
        {pendingValidationData.length > 0 && !showValidationModal && (
          <div className="pending-banner">
            <span className="pending-icon">⚠️</span>
            <span>
              <strong>{pendingValidationData.length} document{pendingValidationData.length > 1 ? 's' : ''} en attente de validation</strong>
              {' '}— Ces documents ne seront pas envoyés vers Airtable tant que vous n'avez pas validé.
            </span>
            <button className="pending-resume-btn" onClick={() => setShowValidationModal(true)}>
              Reprendre la révision →
            </button>
          </div>
        )}

        {stats && extractedData.length > 0 && (
          <div className="stats-banner">
            <div className="stat-item">
              <span className="stat-value">{stats.totalFiles}</span>
              <span className="stat-label">fichiers traités</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <span className="stat-value">{extractedData.length}</span>
              <span className="stat-label">documents extraits et corrigés</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <button className="clear-all-button" onClick={handleClearAll}>
                🗑️ Tout effacer
              </button>
            </div>
          </div>
        )}

        <ImmeubleSelector
          onSelectionComplete={setImmeubleSelection}
          currentSelection={immeubleSelection}
        />

        {immeubleSelection && (
          <UploadZone
            onFilesSelected={handleFilesSelected}
            files={files}
            isProcessing={isProcessing}
            onUpload={handleUpload}
            onReset={handleReset}
            hasExistingData={extractedData.length > 0}
            fileStatuses={fileStatuses}
            processedCount={processedCount}
          />
        )}

        {extractedData.length > 0 && (
          <>
            <DataTable
              data={extractedData}
              onDeleteDocument={handleDeleteDocument}
              onUpdateDocument={handleUpdateDocument}
            />
            <div className="export-actions">
              <ExportButton onExport={handleExport} dataCount={extractedData.length} />
            </div>
          </>
        )}
      </main>

      <footer className="footer">
        <p>© 2026 AssurExtract - Propulsé par Claude Vision API</p>
      </footer>

      {/* Modal de validation */}
      {showValidationModal && pendingValidationData.length > 0 && (
        <ValidationModal
          data={pendingValidationData}
          onValidate={handleValidate}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
}

export default App;
